import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useContentFooter, useContentHeader } from "hooks/mainContent";
import { useHistory, useLocation, useParams } from "react-router-dom";
import SearchAsset from "./SearchAsset";
import { emptyArray, emptyObject } from "constants/defaultValues";
import EmptyAssetList from "./EmptyAssetList";
import AssetList from "./AssetList";
import QuoteStepper from "features/quote/components/QuoteStepper";
import { quoteSteps } from "features/quote/constants";
import QuoteFooter from "features/quote/components/QuoteFooter";
import { useApolloClient, useMutation } from "@apollo/client";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { ADD_QUOTE, ADD_QUOTE_ASSET, UPDATE_QUOTE } from "../mutations";
import { LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { QUOTE_WITH_ASSETS_QUERY } from "features/quote/queries";
import { invalidateApolloClientCache } from "utils/functions";
import { Grid } from "@material-ui/core";
import { AccountBox, CheckBox } from "@material-ui/icons";
import AssetsWarning from "./AssetsWarning";
import { isDirty } from "@totalsoft/change-tracking";
import { useRulesLens } from "@totalsoft/rules-algebra-react";
import { getQuoteRules, getQuoteValidator } from "../rules";
import { get, set, useStateLens } from "@totalsoft/react-state-lens";
import * as R from "@totalsoft/rules-algebra";
import EstimationHeaderCard from "./EstimationHeaderCard";
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import useUserData from "hooks/useUserData";
import { eurCurrency } from "constants/currency";
import { isValid } from "@totalsoft/pure-validations-react";
import { addToast } from "components/notifications";
import { SAVE_QUOTE_PARTNERS } from "features/client/mutations";
import { EXECUTE_ZOOT_RISK_CALL, EXECUTE_ZOOT_SCORING_AUTO_CALL } from "features/quote/mutations";

function AssetsStepContainer() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const client = useApolloClient();
  const params = useParams();
  const quoteId = Number(params.quoteId);
  const quoteExists = Boolean(quoteId);
  const [, setHeader] = useContentHeader();
  const [, setFooter] = useContentFooter();
  const openDialogLens = useStateLens(false);

  const quoteRules = useMemo(() => getQuoteRules(), []);
  const [quoteLens, quoteDirtyInfo, resetQuoteLens] = useRulesLens(quoteRules, {
    quoteAssets: emptyArray,
    partnerId: location?.state?.clientId,
    isEstimation: location.pathname === "/estimations/new/assets",
  });
  const quote = quoteLens |> get;
  const quoteIsDirty = isDirty(quoteDirtyInfo);
  const quoteIsEstimation = quote?.isEstimation || location.pathname === "/estimations/new/assets";

  const { data, loading: loadingQuote } = useQueryWithErrorHandling(QUOTE_WITH_ASSETS_QUERY, {
    variables: { quoteId: parseInt(quoteId) },
    skip: !quoteExists,
  });

  const { isExchangeRateFunctionality } = useUserData();
  const showValuesInEuro = (quote && quote?.currencyCode !== eurCurrency.code && isExchangeRateFunctionality) || false;

  const validator = useMemo(() => getQuoteValidator(showValuesInEuro), [showValuesInEuro]);
  const [validation, validate] = useDirtyFieldValidation(validator);

  useEffect(() => {
    const model = quoteLens |> get;
    if (!validate(model)) return;
  }, [validate, quoteLens]);

  useEffect(() => {
    if (!data?.quote || quoteIsDirty) return;
    const quote = R.applyRule(quoteRules, data.quote, emptyObject);
    resetQuoteLens(quote);
  }, [resetQuoteLens, data?.quote, quoteIsDirty, quoteRules, location?.state?.clientId]);

  useEffect(() => {
    if (location.state?.message) addToast(location.state.message, "success");
    if (location.state?.errorMessage) addToast(location.state.errorMessage, "warning");
  }, [t, location]);

  const [addQuote, { loading: savingAddQuote }] = useMutationWithErrorHandling(ADD_QUOTE);
  const [saveQuotePartners] = useMutationWithErrorHandling(SAVE_QUOTE_PARTNERS);
  const [addQuoteAsset, { loading: savingAddQuoteAsset }] = useMutationWithErrorHandling(ADD_QUOTE_ASSET, {});

  const handleAdd = useCallback(
    async (asset) => {
      const newQuoteAsset = {
        assetId: asset.assetId,
        qtty: 1,
        conditionTypeId: asset.conditionTypeId,
        manufacturingYear: asset.manufacturingYear,
        operatingHours: asset.operatingHours,
        brandId: asset.brandId,
        priceCards: asset.priceCards,
      };
      if (quoteExists) {
        const quoteAssets = quote.quoteAssets.map((a) => ({
          quoteAssetId: a.quoteAssetId,
          assetId: a.assetId,
          qtty: a.qtty,
          assetNetPrice: a.assetNetPrice,
          assetNetPriceWithVAT: a.assetNetPriceWithVAT,
          additionalCosts: a.additionalCosts,
          additionalCostsWithVAT: a.additionalCostsWithVAT,
          conditionTypeId: a.conditionTypeId,
          manufacturingYear: a.manufacturingYear,
          operatingHours: a.operatingHours,
          assetDescription: a.assetDescription,
          supplierId: a.supplierId,
          brandId: a.brandId,
          priceCards: a.priceCards ?? emptyArray,
        }));
        await addQuoteAsset({
          variables: {
            input: {
              quoteId,
              estimationName: quote?.estimationName,
              currencyId: quote.currencyId,
              exchangeRate: quote?.exchangeRate,
              exchangeRateDate: quote?.exchangeRateDate,
              assets: [...quoteAssets, newQuoteAsset],
            },
          },
        });
        resetQuoteLens(quote);
        invalidateApolloClientCache(client, { __typename: "Quote", quoteId }, [
          "quoteAssets",
          "quoteProducts",
          "currencyId",
          "currencyCode",
        ]);
      } else {
        const { data } = await addQuote({
          variables: {
            input: {
              partnerId: location.state?.clientId,
              estimationName: quote?.estimationName,
              currencyId: quote.currencyId,
              exchangeRate: quote?.exchangeRate,
              exchangeRateDate: quote?.exchangeRateDate,
              asset: newQuoteAsset,
            },
          },
        });
        const quoteId = data.addQuote;

        const quotePartners = location.state?.quotePartners || emptyArray;
        if (quotePartners.length > 0) {
          await saveQuotePartners({
            variables: {
              input: {
                quoteId,
                partnerId: location.state?.clientId,
                partners: quotePartners.map((p) => {
                  return { quoteId, partnerId: p.partnerId, roleId: p.roleId };
                }),
              },
            },
          });
        }
        resetQuoteLens(quote);
        invalidateApolloClientCache(client, { __typename: "Quote", quoteId }, [
          "quoteAssets",
          "quoteProducts",
          "currencyId",
          "currencyCode",
        ]);
        history.push({ pathname: `/quotes/${quoteId}/assets` });
      }
    },
    [history, quote, quoteId, location, quoteExists, client, resetQuoteLens, addQuote, addQuoteAsset, saveQuotePartners]
  );

  const [updateQuote] = useMutationWithErrorHandling(UPDATE_QUOTE);
  const [executeZootRiskCall] = useMutation(EXECUTE_ZOOT_RISK_CALL);
  const [executeZootScoringAutoCall] = useMutation(EXECUTE_ZOOT_SCORING_AUTO_CALL);

  const handleBack = useCallback(async () => {
    if (quoteExists && (quoteLens.quoteAssets |> get).length > 0) {
      (openDialogLens |> set)(true);
    } else if (quoteExists) {
      await updateQuote({ variables: { input: { quoteId: quote.quoteId, assets: emptyArray } } });
      invalidateApolloClientCache(client, { __typename: "Quote", quoteId: quote.quoteId }, ["quoteAssets"]);
      history.push({
        pathname: `/quotes/${quote.quoteId}/client`,
        state: { partnerList: location?.state?.partnerList },
      });
    } else {
      history.push({ pathname: `/quotes/new/client`, state: { partnerList: location?.state?.partnerList } });
    }
  }, [history, quoteExists, openDialogLens, location.state, quoteLens.quoteAssets, quote.quoteId, updateQuote, client]);

  const handleNext = useCallback(async () => {
    if (isDirty(quoteDirtyInfo)) {
      const input = {
        quoteId: quote.quoteId,
        partnerId: quote.partnerId,
        quoteHeader: {
          estimationName: quote?.estimationName,
          currencyId: quote.currencyId,
          exchangeRate: quote?.exchangeRate,
          exchangeRateDate: quote?.exchangeRateDate,
        },
        assets: quote.quoteAssets.map((a) => ({
          quoteAssetId: a.quoteAssetId,
          qtty: a.qtty,
          assetNetPrice: a.assetNetPrice,
          assetNetPriceWithVAT: a.assetNetPriceWithVAT,
          additionalCosts: a.additionalCosts,
          additionalCostsWithVAT: a.additionalCostsWithVAT,
          conditionTypeId: a.conditionTypeId,
          manufacturingYear: a.manufacturingYear,
          operatingHours: a.operatingHours,
          assetDescription: a.assetDescription,
          supplierId: a.supplierId,
        })),
      };
      const updatedQuote = await updateQuote({ variables: { input } });
      invalidateApolloClientCache(client, { __typename: "Quote", quoteId }, ["quoteAssets", "quoteProducts"]);
      if (!quote.isEstimation) {
        executeZootRiskCall({ variables: { quoteId: quote.quoteId, currentStep: -2 } });
        const updatedQuotePriceCards = updatedQuote?.data?.updateQuote?.priceCards || [];
        const minPriority = Math.min(...updatedQuotePriceCards.map((pc) => pc.priority));
        updatedQuotePriceCards.some((pc) => pc.priority === minPriority && pc.allowRiskBasePricing) &&
          (await executeZootScoringAutoCall({ variables: { quoteId: quote.quoteId } }));
      }
    }
    history.push({ pathname: `/quotes/${quote.quoteId}/products` });
  }, [
    client,
    executeZootRiskCall,
    executeZootScoringAutoCall,
    history,
    quote.currencyId,
    quote?.estimationName,
    quote?.exchangeRate,
    quote?.exchangeRateDate,
    quote.isEstimation,
    quote.partnerId,
    quote.quoteAssets,
    quote.quoteId,
    quoteDirtyInfo,
    quoteId,
    updateQuote,
  ]);

  useEffect(() => {
    setHeader(
      <QuoteStepper
        quoteId={quoteId}
        activeStepId={quoteSteps.assets}
        nextStepId={quoteSteps.financeProduct}
        disabledNextStep={
          !quoteExists ||
          quote.quoteAssets.length === 0 ||
          quote.quoteAssets.some(
            (a) =>
              (!a.assetNetPrice || a.assetNetPrice === 0) && (!a.assetNetPriceWithVAT || a.assetNetPriceWithVAT === 0)
          ) ||
          !validate(quote)
        }
        onHandleNextStep={handleNext}
      />
    );
  }, [t, setHeader, quoteId, quoteExists, handleNext, quote, quoteDirtyInfo, validate]);

  useEffect(() => {
    setFooter(
      <QuoteFooter
        btnBackLabel={t("General.Client")}
        btnNextLabel={t("General.SelectProduct")}
        btnBackIcon={<AccountBox />}
        btnNextIcon={<CheckBox />}
        handleBack={handleBack}
        handleComplete={handleNext}
        btnBackDisabled={quoteIsEstimation}
        btnNextDisabled={
          !quoteExists ||
          !quote?.quoteAssets?.length ||
          quote.quoteAssets.some((a) => (!a.assetNetPrice && !a.assetNetPriceWithVAT) || !a.qtty) ||
          !validate(quote)
        }
        quoteId={quoteId}
      />
    );
  }, [t, setFooter, handleBack, handleNext, quoteExists, quoteId, quote, validate, quoteIsEstimation]);

  const loading = loadingQuote;
  const saving = savingAddQuote || savingAddQuoteAsset;

  if (loading) {
    return <LoadingFakeText lines={10} />;
  }

  return (
    <>
      <Grid container spacing={2}>
        {quoteIsEstimation && (
          <Grid item xs={12}>
            <EstimationHeaderCard quoteLens={quoteLens} disabled={!quote} validation={validation} />
          </Grid>
        )}
        <Grid item xs={12}>
          <SearchAsset
            quoteLens={quoteLens}
            disabled={
              [-2, -4, -5, -6, -7, -8, -9, -10, -11, -12, -13].indexOf(quote.quoteStatusId) > -1 ||
              (quoteIsEstimation && !isValid(validation.estimationName))
            }
            currencyId={quote.currencyId}
            onAdd={handleAdd}
            saving={saving}
          />
        </Grid>
        {quote?.quoteAssets?.length ? (
          <Grid item xs={12}>
            <AssetList quoteLens={quoteLens} validation={validation} showValuesInEuro={showValuesInEuro} />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <EmptyAssetList />
          </Grid>
        )}
      </Grid>
      <AssetsWarning openDialogLens={openDialogLens} quoteId={quoteId} />
    </>
  );
}

export default AssetsStepContainer;
